import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { inject, Service } from '@angular/core';
import { marker } from '@colsen1991/ngx-translate-extract-marker';
import { catchError, map, Observable, throwError } from 'rxjs';
import { Box, BoxDto } from '@app/store/placed-boxes/models';
import { WarehouseError } from '@app/store/shared/models';
import { GenerateCartonsDto } from '../models';
import { mapDto } from '@shared/operators';
import { ApiResponse, hasMore, PaginatedApiResponse } from '@shared/models/api-response';
import { uuid } from '@shared/utils/uuid';

const PAGE_SIZE = 20;

@Service()
export class HttpService {
    private readonly httpClient = inject(HttpClient);

    loadBoxes(page: number): Observable<{ boxes: Box[]; hasMore: boolean }> {
        return this.httpClient
            .get<PaginatedApiResponse<BoxDto[]>>('cartons', { params: { status: 'loose', sort: 'createdAt', order: 'desc', page, limit: PAGE_SIZE } })
            .pipe(
                mapDto((response) => ({
                    boxes: response.data.map((dto) => new Box(dto)),
                    hasMore: hasMore(response),
                })),
            );
    }

    generateBoxes(quantity: number): Observable<Box[]> {
        return this.httpClient.post<ApiResponse<GenerateCartonsDto>>('cartons', { quantity }).pipe(
            mapDto((response) => response.data.cartons.map((dto) => new Box(dto))),
            catchError((response: HttpErrorResponse) => throwError(() => new WarehouseError(response, marker('boxes.new.error')))),
        );
    }

    printCarton(code: string): Observable<void> {
        return this.httpClient
            .skipErrorMessageInterceptor()
            .post(`cartons/${code}/print`, { jobKey: uuid() })
            .pipe(
                map(() => undefined),
                catchError((response: HttpErrorResponse) => throwError(() => new WarehouseError(response, marker('boxes.new.print-error')))),
            );
    }

    assignBox(cartonCode: string, cellCode: string): Observable<Box> {
        return this.httpClient.post<ApiResponse<BoxDto>>('cartons/assign', { cartonCode, cellCode }).pipe(
            mapDto((response) => new Box(response.data)),
            catchError((response: HttpErrorResponse) => throwError(() => new WarehouseError(response, marker('boxes.assign.error')))),
        );
    }
}
